import { readonly, ref, watch, type Ref, type WatchStopHandle } from 'vue'

/**
 * Keeps a modal out of the page's startup module graph until the user first
 * opens it.
 *
 * `defineAsyncComponent` is lazy about the *bundle*, but the fetch fires on
 * first render — so an always-rendered async modal loads its chunk during boot
 * anyway. The `v-if` this gate feeds is what actually defers it. That matters
 * for the deposit and withdraw modals, which share the LiFi widget's React and
 * wallet stack: ~2.1 MB that was executing during startup and costing ~502 ms
 * of INP input delay on `/` (#819).
 *
 * The flag latches: once opened, the modal stays mounted. Unmounting on close
 * would cut the closing transition short and re-parse the chunk on every
 * reopen, and the load has already been paid for by then.
 *
 * It stays latched even when the chunk failed to load. An earlier version
 * un-latched there, on the theory that a fresh async component would retry the
 * import; it cannot, and the reasoning is in `utils/asyncModalStates`.
 *
 * `immediate` is defensive: today this is called from `app.vue`, whose setup
 * runs before any page's, so the flag is always false at that point. It matters
 * if the gate is ever created from a later scope, and it costs nothing. (The
 * `immediate` on `DepositModal`'s own open-time watcher is a different thing
 * entirely, and that one IS load-bearing — a gated modal mounts already open,
 * so a plain watcher would never see the false→true edge.)
 *
 * Call from a component setup or an `effectScope`; the watcher is disposed with
 * the owning scope.
 */
export function mountOnceOpened(isOpen: () => boolean): Readonly<Ref<boolean>> {
  const mounted = ref(false)

  // `let`, not `const`: with `immediate` the callback runs synchronously inside
  // watch(), before the binding is initialised, and a `const` in its temporal
  // dead zone throws on access — optional chaining does not save you there.
  // The `= undefined` looks redundant and is not: without it eslint sees a
  // single assignment and `prefer-const` demands the form that throws.
  let stop: WatchStopHandle | undefined = undefined
  stop = watch(
    isOpen,
    (open) => {
      if (!open) return
      mounted.value = true
      // The latch is one-way, so once it has flipped there is nothing left to
      // watch. Undefined on the immediate pass; the line below covers that.
      stop?.()
    },
    { immediate: true }
  )
  if (mounted.value) stop()

  return readonly(mounted)
}
